import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import BankAccountsEditor from '@/components/BankAccountsEditor';

export function PlatformFormDialog({ open, onOpenChange, editingPlatform, platformForm, setPlatformForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingPlatform ? 'Edit Platform' : 'Add Delivery Platform'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Platform Name *</Label>
              <Input value={platformForm.name} onChange={(e) => setPlatformForm({ ...platformForm, name: e.target.value })} placeholder="e.g., HungerStation" required />
            </div>
            <div>
              <Label>Arabic Name</Label>
              <Input value={platformForm.name_ar} onChange={(e) => setPlatformForm({ ...platformForm, name_ar: e.target.value })} placeholder="e.g., هنقرستيشن" dir="rtl" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Commission Rate (%)</Label>
              <Input type="number" step="0.1" value={platformForm.commission_rate} onChange={(e) => setPlatformForm({ ...platformForm, commission_rate: e.target.value })} placeholder="e.g., 20" />
            </div>
            <div>
              <Label>Payment Terms</Label>
              <Select value={platformForm.payment_terms} onValueChange={(val) => setPlatformForm({ ...platformForm, payment_terms: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <AgreementFields platformForm={platformForm} setPlatformForm={setPlatformForm} editing={!!editingPlatform} />
          <div className="border-t pt-3">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bank Accounts (optional)</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
              Add the bank account(s) the platform settles into. Once added, incoming bank-statement transfers from this platform are auto-matched by IBAN/account name.
            </p>
            <BankAccountsEditor
              value={platformForm.bank_accounts || []}
              onChange={(arr) => setPlatformForm({ ...platformForm, bank_accounts: arr })}
              testidPrefix="platform-bank"
            />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={platformForm.notes} onChange={(e) => setPlatformForm({ ...platformForm, notes: e.target.value })} placeholder="Additional notes..." rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-purple-600 hover:bg-purple-700">{editingPlatform ? 'Update' : 'Add'} Platform</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AgreementFields({ platformForm, setPlatformForm, editing }) {
  const set = (k) => (e) => setPlatformForm({ ...platformForm, [k]: e.target.value });
  return (
    <div className="grid grid-cols-3 gap-4 rounded-lg border border-dashed p-3" data-testid="platform-agreement-fields">
      <div>
        <Label>VAT on commission (%)</Label>
        <Input type="number" step="0.01" value={platformForm.commission_vat_rate ?? ''} onChange={set('commission_vat_rate')} placeholder="e.g., 15" data-testid="platform-commission-vat" />
      </div>
      <div>
        <Label>Commission charged on</Label>
        <Select value={platformForm.commission_basis || 'unknown'} onValueChange={(v) => setPlatformForm({ ...platformForm, commission_basis: v === 'unknown' ? null : v })}>
          <SelectTrigger data-testid="platform-commission-basis"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="gross_incl_vat">Sales incl. VAT</SelectItem>
            <SelectItem value="net_excl_vat">Sales excl. VAT</SelectItem>
            <SelectItem value="unknown">Not confirmed yet</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{editing ? 'New terms effective from' : 'Effective from'}</Label>
        <Input type="date" value={platformForm.effective_from || ''} onChange={set('effective_from')} data-testid="platform-effective-from" />
      </div>
      <p className="col-span-3 text-[11px] text-muted-foreground">Changing the rate, VAT or basis keeps the old terms for earlier periods. An unconfirmed basis is compared provisionally (incl. VAT) and flagged.</p>
    </div>
  );
}
